// CRIATIVOS (Marketing → Criativos), 14/09/2026.
//
// Painel de arquivos do marketing: a Débora e o time sobem artes 1:1, 9:16 e
// vídeos e organizam em pastas. A pasta fixa "App das consultoras" é a que o
// app Luísa (app.twplanalto) mostra na aba Artes — o resto é biblioteca interna.
//
// Backend: server/routes/criativos.cjs. Três coisas moldam esta tela:
//  - o ENVIO é em partes (4 MB) direto para o disco, para vídeo de até 500 MB
//    não esbarrar no limite de 100 MB nem no tempo de leitura do proxy;
//  - as partes vão por `fetch` DIRETO com o token, e não pelo
//    `authenticatedFetch`: ele força `Content-Type: application/json`, e o
//    `express.json` global do servidor tentaria ler o pedaço de vídeo como JSON;
//  - miniatura e arquivo vêm com LINK ASSINADO na listagem, porque <img> e
//    <video> não mandam o cabeçalho de login.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  FiFolder, FiFolderPlus, FiUploadCloud, FiLoader, FiChevronRight, FiSearch, FiX, FiPlay,
  FiDownload, FiEdit2, FiTrash2, FiSmartphone, FiAlertTriangle, FiCheck, FiImage, FiFilm,
} from 'react-icons/fi'
import { useAuth } from '../contexts/AuthContext'

type Pasta = { nome: string; itens: number; mtime: number; fixa: boolean }
type Arquivo = {
  nome: string; tipo: 'imagem' | 'video'; tamanho: number; mtime: number
  largura: number | null; altura: number | null; duracao: number | null; formato: string | null
  url: string; miniatura: string
}
type Lista = { caminho: string; fixa: boolean; pastas: Pasta[]; arquivos: Arquivo[]; limite_bytes: number; extensoes: string[] }
type Envio = { chave: string; nome: string; pasta: string; tamanho: number; progresso: number; estado: 'fila' | 'enviando' | 'ok' | 'erro' | 'cancelado'; erro?: string }

const API = '/api/criativos'
const MAX_BYTES = 500 * 1024 * 1024
const EXT = /\.(jpe?g|png|webp|gif|mp4|mov|m4v|webm)$/i

const juntar = (caminho: string, nome: string) => (caminho ? `${caminho}/${nome}` : nome)
const tamanhoTxt = (n: number) => {
  const u = ['B', 'KB', 'MB', 'GB']; let i = 0, v = n
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++ }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`
}
const duracaoTxt = (s: number | null) => (s == null ? '' : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`)
const dataTxt = (t: number) => { try { return new Date(t * 1000).toLocaleDateString('pt-BR') } catch { return '' } }
const espera = (ms: number) => new Promise(r => setTimeout(r, ms))

type Filtro = '' | '1:1' | '9:16' | 'video'

export function CriativosPanel() {
  const { authenticatedFetch, token, user, hasToolAccess } = useAuth()
  const gere = !!user?.isAdmin || hasToolAccess('criativos_gestao')

  const [caminho, setCaminho] = useState('')
  const [lista, setLista] = useState<Lista | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [busca, setBusca] = useState('')
  const [filtro, setFiltro] = useState<Filtro>('')
  const [aberto, setAberto] = useState<Arquivo | null>(null)
  const [novaPasta, setNovaPasta] = useState<string | null>(null)
  const [renomeando, setRenomeando] = useState<{ alvo: string; nome: string } | null>(null)
  const [arrastando, setArrastando] = useState(false)
  const [envios, setEnvios] = useState<Envio[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const filaRef = useRef<{ env: Envio; file: File }[]>([])
  const rodandoRef = useRef(false)
  const cancelarRef = useRef<Set<string>>(new Set())
  const caminhoRef = useRef(caminho)
  caminhoRef.current = caminho

  const carregar = useCallback(async (c: string = caminhoRef.current) => {
    setErro(null)
    try {
      const r = await authenticatedFetch(`${API}/lista?caminho=${encodeURIComponent(c)}`)
      const d = await r.json()
      if (!r.ok || !d.ok) throw new Error(d.error || `HTTP ${r.status}`)
      if (c === caminhoRef.current) setLista(d)
    } catch (e: any) { setErro(e.message || 'Falha ao carregar') }
  }, [authenticatedFetch])

  useEffect(() => { setLista(null); setBusca(''); setFiltro(''); carregar(caminho) }, [caminho, carregar])

  const api = useCallback(async (url: string, init: RequestInit = {}) => {
    const r = await authenticatedFetch(url, init)
    const d = await r.json().catch(() => ({}))
    return { r, d }
  }, [authenticatedFetch])

  /* ── envio ──────────────────────────────────────────────────────────── */

  const atualizar = (chave: string, p: Partial<Envio>) =>
    setEnvios(es => es.map(e => (e.chave === chave ? { ...e, ...p } : e)))

  const enviarUm = useCallback(async (env: Envio, file: File) => {
    atualizar(env.chave, { estado: 'enviando', progresso: 0 })
    const { r, d } = await api(`${API}/envio`, {
      method: 'POST', body: JSON.stringify({ caminho: env.pasta, nome: file.name, tamanho: file.size }),
    })
    if (!r.ok || !d.ok) throw new Error(d.error || `HTTP ${r.status}`)
    const id: string = d.id
    const parte: number = d.parte
    let inicio = 0
    while (inicio < file.size) {
      if (cancelarRef.current.has(env.chave)) {
        await api(`${API}/envio/${id}`, { method: 'DELETE' })
        throw new Error('cancelado')
      }
      let tentativa = 0
      for (;;) {
        try {
          const resp = await fetch(`${API}/envio/${id}?inicio=${inicio}`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/octet-stream' },
            body: file.slice(inicio, inicio + parte),
          })
          const j = await resp.json().catch(() => ({}))
          // Fora de ordem: o servidor diz até onde recebeu, e dali se continua.
          if (resp.status === 409 && typeof j.recebido === 'number') { inicio = j.recebido; break }
          if (!resp.ok) throw Object.assign(new Error(j.error || `HTTP ${resp.status}`), { status: resp.status })
          inicio = j.recebido
          break
        } catch (e: any) {
          // 4xx é recusa de verdade; rede caída e 5xx merecem outra tentativa.
          if ((e.status && e.status < 500) || ++tentativa >= 4) throw e
          await espera(1500 * tentativa)
        }
      }
      atualizar(env.chave, { progresso: Math.min(1, inicio / file.size) })
    }
    const fim = await api(`${API}/envio/${id}/fim`, { method: 'POST', timeout: 120000 } as RequestInit)
    if (!fim.r.ok || !fim.d.ok) throw new Error(fim.d.error || `HTTP ${fim.r.status}`)
  }, [api, token])

  const rodarFila = useCallback(async () => {
    if (rodandoRef.current) return
    rodandoRef.current = true
    try {
      while (filaRef.current.length) {
        const { env, file } = filaRef.current.shift()!
        if (cancelarRef.current.has(env.chave)) { atualizar(env.chave, { estado: 'cancelado' }); continue }
        try {
          await enviarUm(env, file)
          atualizar(env.chave, { estado: 'ok', progresso: 1 })
          if (env.pasta === caminhoRef.current) carregar()
        } catch (e: any) {
          const cancelado = e.message === 'cancelado'
          atualizar(env.chave, { estado: cancelado ? 'cancelado' : 'erro', erro: cancelado ? undefined : e.message })
        }
      }
    } finally { rodandoRef.current = false }
  }, [enviarUm, carregar])

  const enfileirar = (files: FileList | File[]) => {
    const novos: Envio[] = []
    for (const file of Array.from(files)) {
      const chave = `${Date.now()}-${Math.random().toString(36).slice(2)}`
      const env: Envio = { chave, nome: file.name, pasta: caminhoRef.current, tamanho: file.size, progresso: 0, estado: 'fila' }
      if (!EXT.test(file.name)) { novos.push({ ...env, estado: 'erro', erro: 'Só imagem (jpg, png, webp, gif) ou vídeo (mp4, mov, webm)' }); continue }
      if (file.size > MAX_BYTES) { novos.push({ ...env, estado: 'erro', erro: 'Maior que 500 MB' }); continue }
      novos.push(env)
      filaRef.current.push({ env, file })
    }
    setEnvios(es => [...es.filter(e => e.estado === 'fila' || e.estado === 'enviando'), ...novos])
    rodarFila()
  }

  // Fechar a aba no meio de um envio perde o que falta: o navegador avisa.
  const enviando = envios.some(e => e.estado === 'fila' || e.estado === 'enviando')
  useEffect(() => {
    if (!enviando) return
    const h = (ev: BeforeUnloadEvent) => { ev.preventDefault(); ev.returnValue = '' }
    window.addEventListener('beforeunload', h)
    return () => window.removeEventListener('beforeunload', h)
  }, [enviando])

  /* ── pasta, renomear, apagar ────────────────────────────────────────── */

  const criarPasta = async () => {
    const nome = (novaPasta || '').trim()
    if (!nome) { setNovaPasta(null); return }
    const { r, d } = await api(`${API}/pasta`, { method: 'POST', body: JSON.stringify({ caminho, nome }) })
    if (!r.ok || !d.ok) { setErro(d.error || 'Falha ao criar a pasta'); return }
    setNovaPasta(null); carregar()
  }

  const renomear = async () => {
    if (!renomeando) return
    const nome = renomeando.nome.trim()
    const atual = renomeando.alvo.split('/').pop()
    if (!nome || nome === atual) { setRenomeando(null); return }
    const { r, d } = await api(`${API}/renomear`, { method: 'POST', body: JSON.stringify({ caminho: renomeando.alvo, nome }) })
    if (!r.ok || !d.ok) { setErro(d.error || 'Falha ao renomear'); return }
    setRenomeando(null); setAberto(null); carregar()
  }

  const apagar = async (alvo: string, ehPasta: boolean, itens = 0) => {
    const nome = alvo.split('/').pop()
    // A listagem já disse quantos itens a pasta tem: UMA pergunta, com a conta.
    // O 409 do servidor fica de rede de baixo (alguém enviou algo nesse meio-tempo).
    const pergunta = !ehPasta ? `Apagar "${nome}"? Não dá para desfazer.`
      : itens ? `Apagar a pasta "${nome}" e os ${itens} ${itens === 1 ? 'item' : 'itens'} dentro dela? Não dá para desfazer.`
      : `Apagar a pasta "${nome}"?`
    if (!window.confirm(pergunta)) return
    let { r, d } = await api(`${API}/item?caminho=${encodeURIComponent(alvo)}${ehPasta && itens ? '&tudo=1' : ''}`, { method: 'DELETE' })
    if (r.status === 409 && d.itens) {
      if (!window.confirm(`A pasta "${nome}" tem ${d.itens} ${d.itens === 1 ? 'item' : 'itens'} dentro. Apagar tudo junto? Não dá para desfazer.`)) return
      ;({ r, d } = await api(`${API}/item?caminho=${encodeURIComponent(alvo)}&tudo=1`, { method: 'DELETE' }))
    }
    if (!r.ok || !d.ok) { setErro(d.error || 'Falha ao apagar'); return }
    setAberto(null); carregar()
  }

  /* ── o que se vê ────────────────────────────────────────────────────── */

  const trilha = caminho ? caminho.split('/') : []
  const termo = busca.trim().toLowerCase()
  const pastas = useMemo(() => (lista?.pastas || []).filter(p => !termo || p.nome.toLowerCase().includes(termo)), [lista, termo])
  const arquivos = useMemo(() => (lista?.arquivos || []).filter(a =>
    (!termo || a.nome.toLowerCase().includes(termo))
    && (!filtro || (filtro === 'video' ? a.tipo === 'video' : a.formato === filtro))), [lista, termo, filtro])

  const cont = useMemo(() => {
    const a = lista?.arquivos || []
    return { q: a.filter(x => x.formato === '1:1').length, s: a.filter(x => x.formato === '9:16').length, v: a.filter(x => x.tipo === 'video').length }
  }, [lista])

  const chips: { k: Filtro; txt: string }[] = [
    { k: '', txt: `${lista?.arquivos.length ?? 0} ${lista?.arquivos.length === 1 ? 'arquivo' : 'arquivos'}` },
    { k: '1:1', txt: `${cont.q} em 1:1` },
    { k: '9:16', txt: `${cont.s} em 9:16` },
    { k: 'video', txt: `${cont.v} ${cont.v === 1 ? 'vídeo' : 'vídeos'}` },
  ]

  return (
    <div className="h-full min-h-0 flex flex-col gap-3 p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3 shrink-0 flex-wrap">
        <div className="min-w-0">
          <nav className="flex items-center gap-1 text-[15px] font-semibold text-foreground flex-wrap" aria-label="Pastas">
            <button onClick={() => setCaminho('')} className={trilha.length ? 'text-muted-foreground hover:text-foreground' : ''}>Criativos</button>
            {trilha.map((seg, i) => (
              <span key={i} className="inline-flex items-center gap-1 min-w-0">
                <FiChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                <button onClick={() => setCaminho(trilha.slice(0, i + 1).join('/'))}
                  className={`truncate max-w-[16rem] ${i < trilha.length - 1 ? 'text-muted-foreground hover:text-foreground' : ''}`}>{seg}</button>
              </span>
            ))}
          </nav>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            {chips.map(c => (
              <button key={c.txt} onClick={() => setFiltro(f => (f === c.k ? '' : c.k))}
                className={`neu-chip px-2.5 py-0.5 rounded-full text-[11px] tabular-nums transition cursor-pointer hover:text-foreground
                  ${filtro === c.k && (c.k || !filtro) ? 'text-foreground font-semibold' : 'text-muted-foreground'}`}>
                {c.txt}
              </button>
            ))}
            {lista?.fixa && (
              <span className="neu-chip px-2.5 py-0.5 rounded-full text-[11px] inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300 font-semibold">
                <FiSmartphone className="w-3 h-3" /> aparece no app das consultoras
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="neu-inset rounded-full hidden md:flex items-center gap-2 px-3 py-1 w-52">
            <FiSearch className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="buscar nesta pasta"
              className="bg-transparent outline-none text-[12.5px] flex-1 min-w-0" />
            {busca && <button onClick={() => setBusca('')} aria-label="Limpar busca"><FiX className="w-3.5 h-3.5 text-muted-foreground" /></button>}
          </div>
          {gere && (novaPasta === null ? (
            <button onClick={() => setNovaPasta('')}
              className="neu-relief inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[13px] font-medium">
              <FiFolderPlus className="w-4 h-4" /> Nova pasta
            </button>
          ) : (
            <form onSubmit={e => { e.preventDefault(); criarPasta() }} className="neu-inset rounded-full flex items-center gap-1 pl-3 pr-1 py-0.5">
              <input autoFocus value={novaPasta} onChange={e => setNovaPasta(e.target.value)} placeholder="nome da pasta"
                onKeyDown={e => { if (e.key === 'Escape') setNovaPasta(null) }} maxLength={120}
                className="bg-transparent outline-none text-[12.5px] w-40" />
              <button type="submit" className="p-1.5 rounded-full hover:bg-muted/60" aria-label="Criar pasta"><FiCheck className="w-4 h-4" /></button>
              <button type="button" onClick={() => setNovaPasta(null)} className="p-1.5 rounded-full hover:bg-muted/60" aria-label="Cancelar"><FiX className="w-4 h-4" /></button>
            </form>
          ))}
          {gere && (
            <button onClick={() => inputRef.current?.click()}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[13px] font-medium bg-primary text-primary-foreground shadow-sm hover:opacity-90">
              <FiUploadCloud className="w-4 h-4" /> Enviar
            </button>
          )}
          <input ref={inputRef} type="file" multiple className="hidden"
            accept=".jpg,.jpeg,.png,.webp,.gif,.mp4,.mov,.m4v,.webm,image/*,video/*"
            onChange={e => { const f = Array.from(e.target.files || []); e.target.value = ''; if (f.length) enfileirar(f) }} />
        </div>
      </div>

      {erro && (
        <div className="shrink-0 px-3 py-2 rounded-lg bg-destructive/10 text-destructive text-xs flex items-center gap-2">
          <FiAlertTriangle className="w-3.5 h-3.5 shrink-0" /><span className="flex-1">{erro}</span>
          <button onClick={() => setErro(null)} aria-label="Fechar aviso"><FiX className="w-3.5 h-3.5" /></button>
        </div>
      )}

      <div
        onDragOver={gere ? (e => { e.preventDefault(); setArrastando(true) }) : undefined}
        onDragLeave={e => { if (e.currentTarget === e.target) setArrastando(false) }}
        onDrop={gere ? (e => { e.preventDefault(); setArrastando(false); if (e.dataTransfer.files.length) enfileirar(e.dataTransfer.files) }) : undefined}
        className={`criativos-area relative flex-1 min-h-0 overflow-y-auto rounded-xl transition-colors [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
          ${arrastando ? 'outline-2 outline-dashed outline-primary bg-primary/5' : ''}`}>
        {lista === null && !erro && (
          <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground text-sm">
            <FiLoader className="w-4 h-4 animate-spin" /> Carregando…
          </div>
        )}

        {lista && !pastas.length && !arquivos.length && (
          <div className="flex flex-col items-center justify-center text-center gap-2 py-16 px-6 text-muted-foreground">
            <FiImage className="w-8 h-8 opacity-50" />
            <p className="text-sm font-medium text-foreground">
              {termo || filtro ? 'Nada com esse filtro nesta pasta' : 'Pasta vazia'}
            </p>
            {gere && !termo && !filtro && (
              <p className="text-[12.5px] max-w-sm">Arraste imagens e vídeos para cá, ou use <strong>Enviar</strong>. Vídeo até 500 MB.</p>
            )}
            {lista.fixa && !termo && !filtro && (
              <p className="text-[12.5px] max-w-sm">O que for colocado aqui aparece para as consultoras na aba <strong>Artes</strong> do app.</p>
            )}
          </div>
        )}

        {lista && (pastas.length > 0 || arquivos.length > 0) && (
          <div className="grid gap-3 p-1" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(140px, 44%), 1fr))' }}>
            {pastas.map(p => {
              const alvo = juntar(caminho, p.nome)
              const editando = renomeando?.alvo === alvo
              return (
                <div key={p.nome} className="neu-card group relative rounded-2xl p-3 flex flex-col gap-2 min-w-0">
                  <button onClick={() => !editando && setCaminho(alvo)} className="flex flex-col items-start gap-2 text-left min-w-0">
                    <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${p.fixa ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'bg-primary/10 text-primary'}`}>
                      {p.fixa ? <FiSmartphone className="w-5 h-5" /> : <FiFolder className="w-5 h-5" />}
                    </span>
                    {!editando && <span className="text-[13px] font-semibold text-foreground truncate w-full" title={p.nome}>{p.nome}</span>}
                    <span className="text-[11px] text-muted-foreground tabular-nums">
                      {p.itens} {p.itens === 1 ? 'item' : 'itens'}{p.fixa ? ' · no app' : ''}
                    </span>
                  </button>
                  {editando && (
                    <form onSubmit={e => { e.preventDefault(); renomear() }} className="absolute left-3 right-3 top-[3.75rem]">
                      <input autoFocus value={renomeando!.nome} maxLength={120}
                        onChange={e => setRenomeando({ alvo, nome: e.target.value })}
                        onKeyDown={e => { if (e.key === 'Escape') setRenomeando(null) }} onBlur={renomear}
                        className="w-full neu-inset rounded-lg px-2 py-1 text-[13px] outline-none" />
                    </form>
                  )}
                  {gere && !p.fixa && !editando && (
                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
                      <button onClick={() => setRenomeando({ alvo, nome: p.nome })} className="p-1.5 rounded-lg hover:bg-muted/70" aria-label={`Renomear ${p.nome}`} title="Renomear"><FiEdit2 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => apagar(alvo, true, p.itens)} className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive" aria-label={`Apagar ${p.nome}`} title="Apagar"><FiTrash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </div>
              )
            })}

            {arquivos.map(a => (
              <button key={a.nome} onClick={() => setAberto(a)}
                className="neu-card group rounded-2xl overflow-hidden flex flex-col text-left min-w-0">
                <span className="relative block bg-muted/40" style={{ aspectRatio: '1 / 1' }}>
                  <img src={a.miniatura} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-contain" />
                  {a.tipo === 'video' && (
                    <span className="absolute left-2 bottom-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-black/65 text-white text-[11px] tabular-nums">
                      <FiPlay className="w-3 h-3" />{duracaoTxt(a.duracao)}
                    </span>
                  )}
                  {a.formato && (
                    <span className={`absolute right-2 top-2 px-1.5 py-0.5 rounded-md text-[11px] font-semibold tabular-nums
                      ${a.formato === '1:1' || a.formato === '9:16' ? 'bg-primary text-primary-foreground' : 'bg-black/65 text-white'}`}>
                      {a.formato}
                    </span>
                  )}
                </span>
                <span className="px-3 py-2 min-w-0">
                  <span className="block text-[12.5px] font-medium text-foreground truncate" title={a.nome}>{a.nome}</span>
                  <span className="block text-[11px] text-muted-foreground tabular-nums">{tamanhoTxt(a.tamanho)} · {dataTxt(a.mtime)}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {envios.length > 0 && (
        <div className="shrink-0 neu-card rounded-2xl p-3 max-h-44 overflow-y-auto [scrollbar-width:none]" aria-live="polite">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[12px] font-semibold text-foreground">
              {enviando ? 'Enviando…' : 'Envios'}
            </span>
            {!enviando && (
              <button onClick={() => setEnvios([])} className="text-[11px] text-muted-foreground hover:text-foreground">limpar</button>
            )}
          </div>
          <ul className="flex flex-col gap-1.5">
            {envios.map(e => (
              <li key={e.chave} className="flex items-center gap-2 text-[12px] min-w-0">
                {e.nome.match(/\.(mp4|mov|m4v|webm)$/i) ? <FiFilm className="w-3.5 h-3.5 shrink-0 text-muted-foreground" /> : <FiImage className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />}
                <span className="truncate flex-1 min-w-0" title={e.nome}>{e.nome}</span>
                <span className="text-[11px] text-muted-foreground tabular-nums shrink-0">{tamanhoTxt(e.tamanho)}</span>
                {e.estado === 'enviando' && (
                  <span className="w-24 h-1.5 rounded-full bg-muted overflow-hidden shrink-0" role="progressbar" aria-valuenow={Math.round(e.progresso * 100)} aria-valuemin={0} aria-valuemax={100}>
                    <span className="block h-full bg-primary transition-[width]" style={{ width: `${Math.round(e.progresso * 100)}%` }} />
                  </span>
                )}
                {e.estado === 'enviando' && <span className="w-9 text-right tabular-nums text-[11px] shrink-0">{Math.round(e.progresso * 100)}%</span>}
                {e.estado === 'fila' && <span className="text-[11px] text-muted-foreground shrink-0">na fila</span>}
                {e.estado === 'ok' && <FiCheck className="w-4 h-4 text-emerald-600 shrink-0" aria-label="enviado" />}
                {e.estado === 'cancelado' && <span className="text-[11px] text-muted-foreground shrink-0">cancelado</span>}
                {e.estado === 'erro' && <span className="text-[11px] text-destructive shrink-0 truncate max-w-[16rem]" title={e.erro}>{e.erro}</span>}
                {(e.estado === 'fila' || e.estado === 'enviando') && (
                  <button onClick={() => { cancelarRef.current.add(e.chave); if (e.estado === 'fila') atualizar(e.chave, { estado: 'cancelado' }) }}
                    className="p-1 rounded hover:bg-muted/70 shrink-0" aria-label={`Cancelar ${e.nome}`}><FiX className="w-3.5 h-3.5" /></button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {aberto && (
        <Visualizar
          arquivo={aberto} caminho={juntar(caminho, aberto.nome)} gere={gere}
          renomeando={renomeando} setRenomeando={setRenomeando} onRenomear={renomear}
          onApagar={() => apagar(juntar(caminho, aberto.nome), false)} onFechar={() => { setAberto(null); setRenomeando(null) }}
        />
      )}
    </div>
  )
}

function Visualizar({ arquivo: a, caminho, gere, renomeando, setRenomeando, onRenomear, onApagar, onFechar }: {
  arquivo: Arquivo; caminho: string; gere: boolean
  renomeando: { alvo: string; nome: string } | null
  setRenomeando: (v: { alvo: string; nome: string } | null) => void
  onRenomear: () => void; onApagar: () => void; onFechar: () => void
}) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape' && !renomeando) onFechar() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onFechar, renomeando])
  const editando = renomeando?.alvo === caminho
  // PORTAL (14/09/2026). Dentro do painel, a janela ficava ATRÁS da barra
  // lateral com qualquer z-index: o primeiro `.neu-lab` do DOM é a COLUNA de
  // conteúdo, que abre camada própria (`relative z-10`), e a barra (`z-[70]`)
  // mora fora dela. Medido com elementFromPoint, não chutado. O destino é o
  // PALCO comum aos dois (`.neu-stage`); o invólucro repõe a classe do tema
  // (os `neu-relief`/`neu-inset` são escopados em `.neu-lab`) com fundo
  // transparente e sem posição — não abre camada nenhuma.
  const destino = (document.querySelector('.neu-stage') as HTMLElement | null) || document.body
  const temTema = !!document.querySelector('.neu-lab')
  const info = [a.formato, a.largura && a.altura ? `${a.largura}×${a.altura}` : null, a.tipo === 'video' ? duracaoTxt(a.duracao) : null, tamanhoTxt(a.tamanho)]
    .filter(Boolean).join(' · ')
  return createPortal(
    // Visualização é o caso em que modal serve: não há nada digitado para perder
    // no clique fora (o renomear fecha só por Esc/✓, e o fundo não fecha enquanto edita).
    <div className={temTema ? 'neu-lab' : undefined} style={{ background: 'transparent' }}>
    <div className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center p-4" onClick={() => !editando && onFechar()} role="dialog" aria-modal="true" aria-label={a.nome}>
      <div className="bg-card rounded-2xl shadow-2xl max-w-[min(92vw,1100px)] max-h-[92vh] w-full flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border/60">
          {editando ? (
            <form onSubmit={e => { e.preventDefault(); onRenomear() }} className="flex-1 flex items-center gap-1 min-w-0">
              <input autoFocus value={renomeando!.nome} maxLength={120} onChange={e => setRenomeando({ alvo: caminho, nome: e.target.value })}
                onKeyDown={e => { if (e.key === 'Escape') setRenomeando(null) }}
                className="flex-1 min-w-0 neu-inset rounded-lg px-2 py-1 text-[13px] outline-none" />
              <button type="submit" className="p-1.5 rounded-lg hover:bg-muted/70" aria-label="Salvar nome"><FiCheck className="w-4 h-4" /></button>
              <button type="button" onClick={() => setRenomeando(null)} className="p-1.5 rounded-lg hover:bg-muted/70" aria-label="Cancelar"><FiX className="w-4 h-4" /></button>
            </form>
          ) : (
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-semibold text-foreground truncate" title={a.nome}>{a.nome}</div>
              <div className="text-[11.5px] text-muted-foreground tabular-nums">{info}</div>
            </div>
          )}
          <a href={`${a.url}&baixar=1`} className="neu-relief inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] font-medium">
            <FiDownload className="w-4 h-4" /> Baixar
          </a>
          {gere && !editando && (
            <>
              <button onClick={() => setRenomeando({ alvo: caminho, nome: a.nome })} className="p-2 rounded-full hover:bg-muted/70" aria-label="Renomear" title="Renomear"><FiEdit2 className="w-4 h-4" /></button>
              <button onClick={onApagar} className="p-2 rounded-full hover:bg-destructive/10 text-destructive" aria-label="Apagar" title="Apagar"><FiTrash2 className="w-4 h-4" /></button>
            </>
          )}
          <button onClick={onFechar} className="p-2 rounded-full hover:bg-muted/70" aria-label="Fechar"><FiX className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 min-h-0 bg-black/90 flex items-center justify-center p-2">
          {a.tipo === 'video'
            ? <video src={a.url} controls playsInline preload="metadata" poster={a.miniatura} className="max-w-full max-h-[78vh]" />
            : <img src={a.url} alt={a.nome} className="max-w-full max-h-[78vh] object-contain" />}
        </div>
      </div>
    </div>
    </div>,
    destino,
  )
}
